import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlus, 
  FiBook, 
  FiSearch, 
  FiFilter,
  FiMenu,
  FiMapPin
} from 'react-icons/fi';
import MyNotesSidebar from '../../components/notes/MyNotesSidebar';
import notesApi from '../../services/notesApi';

const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-height: calc(100vh - 2rem);
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 320px 1fr;
    padding: 0 2rem;
  }
  
  @media (min-width: 1280px) {
    padding: 0 2.5rem;
  }
`;

const MainContent = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 400px;
  
  @media (max-width: 1024px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
    min-height: calc(100vh - 80px);
  }
  
  @media (max-width: 640px) {
    padding: 16px;
    min-height: calc(100vh - 80px);
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    min-height: calc(100vh - 80px);
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 640px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-muted);
  margin: 0 0 32px 0;
  max-width: 500px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 100%;
    margin: 0 0 24px 0;
  }
  
  @media (max-width: 640px) {
    font-size: 0.9rem;
    margin: 0 0 20px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin: 0 0 16px 0;
  }
`;

const NewNoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  margin: 0 auto;
  
  &:hover {
    background: var(--primary-dark);
  }
  
  @media (max-width: 640px) {
    padding: 14px 20px;
    font-size: 15px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    gap: 8px;
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 48px;
  max-width: 800px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 32px;
    max-width: 100%;
  }
  
  @media (max-width: 640px) {
    gap: 16px;
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-top: 20px;
  }
`;

const FeatureCard = styled(motion.div)`
  padding: 24px;
  border: 1px solid var(--border-color);
  border-radius: 0;
  text-align: left;
  
  @media (max-width: 640px) {
    padding: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(246, 224, 94, 0.1);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: var(--primary-color);
  
  @media (max-width: 640px) {
    width: 44px;
    height: 44px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    margin-bottom: 10px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  
  @media (max-width: 640px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
  
  @media (max-width: 640px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 1rem;
  background: var(--background-primary);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (min-width: 768px) {
    padding: 16px 1.5rem;
  }
  
  @media (min-width: 1024px) {
    display: none;
  }
  
  @media (min-width: 1280px) {
    padding: 16px 2.5rem;
  }
`;

const MobileTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-secondary);
  cursor: pointer;
  
  &:hover {
    background: var(--background-tertiary);
  }
`;

const NotesList = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const NotesListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const NotesListTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`;

const NotesGrid = styled.div`
  display: grid;
  gap: 16px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
`;

const NoteCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const NoteTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const CourseCode = styled.span`
  background: var(--background-tertiary);
  padding: 2px 8px;
  border-radius: 0;
  font-weight: 500;
`;

const WeekBadge = styled.span`
  background: var(--primary-color);
  color: var(--text-inverse);
  padding: 2px 8px;
  border-radius: 0;
  font-weight: 500;
  font-size: 0.75rem;
`;

const TimeAgo = styled.span`
  color: var(--text-muted);
`;

const NotePreview = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export default function NotesHome() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNewNote = () => {
    navigate('/student/notes/new');
  };

  const handleNoteClick = (note) => {
    navigate(`/student/notes/${note.id}`);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getPlainTextPreview = (contentJson) => {
    if (!contentJson) return '';
    try {
      const content = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      if (content.content && Array.isArray(content.content)) {
        return content.content
          .map(block => {
            if (block.content && Array.isArray(block.content)) {
              return block.content
                .map(item => item.text || '')
                .join('');
            }
            return '';
          })
          .join(' ')
          .substring(0, 100);
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const response = await notesApi.getMyNotes({ pageSize: 50 });
        setNotes(response.items || []);
      } catch (error) {
        console.error('Failed to fetch notes:', error);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, []);

  const features = [
    {
      icon: <FiBook size={24} />,
      title: 'Rich Text Editing',
      description: 'Create beautifully formatted notes with headings, lists, links, and more using our powerful editor.'
    },
    {
      icon: <FiSearch size={24} />,
      title: 'Smart Search',
      description: 'Find your notes instantly with full-text search across titles and content.'
    },
    {
      icon: <FiFilter size={24} />,
      title: 'Organized by Course & Week',
      description: 'Keep your notes organized by course and week for easy reference during your studies.'
    }
  ];

  return (
    <>
      <MobileHeader>
        <MobileTitle>My Notes</MobileTitle>
        <MobileMenuButton onClick={() => setSidebarOpen(true)}>
          <FiMenu size={20} />
        </MobileMenuButton>
      </MobileHeader>

      <PageContainer>
        <MyNotesSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        
        <MainContent>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Loading notes...
            </div>
          ) : notes.length > 0 ? (
            <NotesList>
              <NotesListHeader>
                <NotesListTitle>Your Notes</NotesListTitle>
                <NewNoteButton onClick={handleNewNote}>
                  <FiPlus size={16} />
                  New Note
                </NewNoteButton>
              </NotesListHeader>
              
              <NotesGrid>
                {notes.map((note, index) => (
                  <NoteCard
                    key={note.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => handleNoteClick(note)}
                  >
                    <NoteTitle>
                      {note.is_pinned && <FiMapPin size={14} color="var(--primary-color)" />}
                      {note.title}
                    </NoteTitle>
                    <NoteMeta>
                      <CourseCode>{note.course_code}</CourseCode>
                      <WeekBadge>W{note.week_number}</WeekBadge>
                      <TimeAgo>{formatTimeAgo(note.updated_at)}</TimeAgo>
                    </NoteMeta>
                    <NotePreview>
                      {getPlainTextPreview(note.content_json)}
                    </NotePreview>
                  </NoteCard>
                ))}
              </NotesGrid>
            </NotesList>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <WelcomeTitle>Welcome to Your Notes</WelcomeTitle>
              <WelcomeSubtitle>
                Organize your learning with rich text notes, organized by course and week. 
                Create, edit, and search through your notes effortlessly.
              </WelcomeSubtitle>
              
              <NewNoteButton onClick={handleNewNote}>
                <FiPlus size={20} />
                Create Your First Note
              </NewNoteButton>
              
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <FeatureIcon>
                      {feature.icon}
                    </FeatureIcon>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>{feature.description}</FeatureDescription>
                  </FeatureCard>
                ))}
              </FeaturesList>
            </motion.div>
          )}
        </MainContent>
      </PageContainer>
    </>
  );
}
